const RAW_ORIGIN = import.meta.env.VITE_API_ORIGIN?.trim() ?? '';
const API_ORIGIN = RAW_ORIGIN.replace(/\/$/, '');

export function apiUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${API_ORIGIN}${path}`;
}

type Ok<T> = { success: true; data: T };
type ErrBody = { success: false; error?: { message?: string; code?: string } };

function isErr(j: unknown): j is ErrBody {
  return Boolean(j && typeof j === 'object' && 'success' in j && (j as { success: unknown }).success === false);
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function parseBody(r: Response): Promise<unknown> {
  const t = await r.text();
  if (!t) return null;
  try {
    return JSON.parse(t) as unknown;
  } catch {
    return null;
  }
}

function throwIfBad(r: Response, j: unknown): void {
  if (r.ok && !isErr(j)) return;
  if (j === null && !r.ok) {
    throw new ApiError(
      'No se pudo conectar con el servidor. La PWA necesita un API público (o proxy /api en CloudFront).',
      r.status || 0
    );
  }
  const msg = isErr(j) ? j.error?.message ?? r.statusText : r.statusText || `HTTP ${r.status}`;
  throw new ApiError(msg, r.status, isErr(j) ? j.error?.code : undefined);
}

export async function apiGet<T>(path: string, jwt: string | null, init?: RequestInit): Promise<T> {
  const r = await fetch(apiUrl(path), {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    ...init,
  });
  const j = await parseBody(r);
  throwIfBad(r, j);
  return (j as Ok<T>).data;
}

export async function apiPost<T>(path: string, body: unknown, jwt: string | null): Promise<T> {
  const r = await fetch(apiUrl(path), {
    method: 'POST',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    body: JSON.stringify(body ?? {}),
  });
  const j = await parseBody(r);
  throwIfBad(r, j);
  return (j as Ok<T>).data;
}

export async function apiPut<T>(path: string, body: unknown, jwt: string | null): Promise<T> {
  const r = await fetch(apiUrl(path), {
    method: 'PUT',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    body: JSON.stringify(body ?? {}),
  });
  const j = await parseBody(r);
  throwIfBad(r, j);
  return (j as Ok<T>).data;
}

export async function apiPatch<T>(path: string, body: unknown, jwt: string | null): Promise<T> {
  const r = await fetch(apiUrl(path), {
    method: 'PATCH',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    body: JSON.stringify(body ?? {}),
  });
  const j = await parseBody(r);
  throwIfBad(r, j);
  return (j as Ok<T>).data;
}

export async function apiDeleteJson<T>(path: string, body: unknown, jwt: string | null): Promise<T> {
  const r = await fetch(apiUrl(path), {
    method: 'DELETE',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    body: JSON.stringify(body ?? {}),
  });
  const j = await parseBody(r);
  throwIfBad(r, j);
  return (j as Ok<T>).data;
}
